/**
 * The Team page's capacity card.
 *
 * "Members" is always the real count of active (non-revoked) team rows — every
 * caller can see the roster, so every caller can see this number. The MAXIMUM
 * is a different story: aggregator_team/handler.py enforces it against limits
 * keyed by the portfolio's OWN account, and the only place that value reaches
 * the frontend is GET /signup/subscription-status, which returns the CALLING
 * user's own. That lines up with the portfolio's only when the caller IS the
 * billing owner; for anyone else it would be their unrelated personal account.
 * So this card shows a maximum only for a billing-owner caller, and otherwise
 * shows the used count alone — never a total it cannot vouch for.
 *
 * Renamed from SeatUsageCard in Story #5648 build v2: what a portfolio's team
 * costs is a licensing question, and licensing appears nowhere in this UI.
 * What the Team page needs to say is how many people are on the team and
 * whether another can be added.
 */
const ACCOUNT_MAX_MEMBERS: Record<string, number> = {
  aggregator_starter: 5,
  aggregator_growth: 15,
  aggregator_enterprise: -1,
}

/**
 * Mirrors aggregator_team/handler.py's own default when the account is
 * unknown. Exported for a pure, provider-free unit test.
 */
export function maxMembersForAccount(account: string | null | undefined): number {
  if (!account) return 5
  return ACCOUNT_MAX_MEMBERS[account] ?? 5
}

export interface TeamCapacityCardProps {
  activeMembers: number
  /** Pass only when the caller's own account represents the portfolio's (billing owner). */
  account?: string | null
}

export default function TeamCapacityCard({ activeMembers, account }: TeamCapacityCardProps) {
  const max = account ? maxMembersForAccount(account) : null
  const unlimited = max === -1
  const pct = max && max > 0 ? Math.min(100, Math.round((activeMembers / max) * 100)) : null
  const remaining = max && max > 0 ? Math.max(0, max - safeCount(activeMembers)) : null

  return (
    <div className="light-card p-5" data-testid="team-capacity-card">
      <h3 className="mb-3 text-sm font-semibold text-gray-800">Team</h3>
      <div className="mb-1 flex items-baseline justify-between">
        <span className="text-sm text-gray-600">People on this team</span>
        <span className="text-sm font-semibold text-gray-900" data-testid="team-member-count">
          {max === null ? activeMembers : unlimited ? `${activeMembers} (no maximum)` : `${activeMembers} of ${max}`}
        </span>
      </div>

      {pct !== null && (
        <div className="mb-2 h-2 w-full overflow-hidden rounded-full bg-gray-100" aria-hidden>
          <div
            className={`h-full rounded-full ${pct >= 100 ? 'bg-rose-500' : pct >= 80 ? 'bg-amber-500' : 'bg-iflow-500'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}

      {max === null ? (
        <p className="text-xs text-gray-500">The billing owner can see how many more may be added.</p>
      ) : unlimited ? (
        <p className="text-xs text-gray-500">You can add as many people to this team as you need.</p>
      ) : (
        <p className="text-xs text-gray-500">
          Room for {remaining} more {remaining === 1 ? 'person' : 'people'} on this team.
        </p>
      )}
    </div>
  )
}

/** Never let a bad count produce a negative "room for" number. */
function safeCount(n: number): number {
  return Number.isFinite(n) && n > 0 ? n : 0
}
